/**
 * In-Memory-Handoff fuer Bild-Anhaenge vom Hero-/Landing-Composer zur
 * Chat-Seite.
 *
 * ## Warum
 *
 * Der Landing-Flow (Prozesse-&-HowTos-Seite, Konversationen-Hero) legt die
 * Konversation an und navigiert mit `?initial=<text>` auf `/chat/$id` — ein
 * BILD-Anhang passt nicht in den URL-Param und ging bisher still verloren
 * (der Agent bekam nur den Text; bei der HowTo-Screenshot-Uebernahme riet er
 * dann eine message_id, die der fail-closed-Ownership-Check zu Recht ablehnte).
 *
 * ## Wie
 *
 * Ein simples Modul-Singleton: die Landing-Seite STASHT den Anhang unter der
 * frisch angelegten conversationId, die Chat-Seite NIMMT ihn beim Verarbeiten
 * von `search.initial` genau einmal wieder heraus und schickt ihn mit
 * `chatStream.start(text, attachment, …)` mit. In-Memory ist hier genau
 * richtig: der Handoff ist eine SPA-interne Navigation im selben Moment —
 * ein Reload dazwischen verliert nur den Anhang (der Text uebersteht via URL),
 * exakt das heutige Verhalten, nur ohne den stillen Normalfall-Verlust.
 * `sessionStorage` waere fuer 4-MB-Base64-Bilder ein Quota-Risiko.
 *
 * `conversationId`-gebunden + einmalig konsumierend, damit ein Anhang nie in
 * einer fremden/zweiten Konversation landet.
 */

import type { ChatMessageAttachment } from '@dc-ai-os/api-client/react-query';

/**
 * Bild-Anhang im Chat-API-Shape (`chatStream.start`/`pendingAttachment`).
 * Direkt der erzeugte Typ: der Anhang geht unveraendert an die Schnittstelle,
 * also darf hier auch kein Format stehen, das sie nicht kennt.
 */
export type InitialHandoffAttachment = ChatMessageAttachment;

/**
 * Voll-Handoff einer Erstnachricht: der (ggf. gefaltete Dokument-)Text UND die
 * Bild-Anhaenge. Genutzt vom Startseiten-Composer, wenn Dateien angehaengt
 * sind, dann navigiert er OHNE `?initial`-URL-Param nach `/chat/$id`
 * (Base64-Bilder und lange Dokument-Texte passen nicht in die URL), und die
 * Chat-Seite sendet Text + Anhaenge aus diesem In-Memory-Stash.
 *
 * `attachments` ist eine Liste, seit mehrere Screenshots an eine Nachricht
 * duerfen (Wunsch Tobias 2026-08-28). Leer = keiner.
 */
export interface InitialHandoffMessage {
  text: string;
  attachments: readonly InitialHandoffAttachment[];
}

// Zwei entkoppelte Stashes, damit sich der Anhang-only-Pfad (Prozesse-Landing:
// Text via URL, Bild via Stash) und der Voll-Handoff (Startseite: Text + Anhang
// beide im Stash) NICHT gegenseitig konsumieren.
let attachmentStash: { conversationId: string; attachment: InitialHandoffAttachment } | null = null;
let messageStash: { conversationId: string; message: InitialHandoffMessage } | null = null;

/**
 * Legt den Anhang fuer die angegebene Konversation ab (ueberschreibt einen
 * etwaigen aelteren Stash — es gibt immer nur EINEN Handoff in flight). Der Text
 * laeuft in diesem Pfad ueber den `?initial`-URL-Param (Prozesse-Landing).
 */
export function stashInitialAttachment(
  conversationId: string,
  attachment: InitialHandoffAttachment,
): void {
  attachmentStash = { conversationId, attachment };
}

/**
 * Nimmt den Anhang fuer die Konversation heraus — genau einmal. `null`, wenn
 * keiner (mehr) da ist oder der Stash zu einer ANDEREN Konversation gehoert
 * (der fremde Stash bleibt dann unangetastet liegen).
 */
export function takeInitialAttachment(conversationId: string): InitialHandoffAttachment | null {
  if (attachmentStash === null || attachmentStash.conversationId !== conversationId) {
    return null;
  }
  const attachment = attachmentStash.attachment;
  attachmentStash = null;
  return attachment;
}

/**
 * Legt die vollstaendige Erstnachricht (Text + optionaler Bild-Anhang) fuer die
 * Konversation ab. Ueberschreibt einen aelteren Voll-Handoff.
 */
export function stashInitialMessage(conversationId: string, message: InitialHandoffMessage): void {
  messageStash = { conversationId, message };
}

/**
 * Nimmt die Erstnachricht fuer die Konversation heraus — genau einmal. `null`,
 * wenn keine (mehr) da ist oder der Stash zu einer ANDEREN Konversation gehoert
 * (der fremde Stash bleibt dann unangetastet liegen).
 */
export function takeInitialMessage(conversationId: string): InitialHandoffMessage | null {
  if (messageStash === null || messageStash.conversationId !== conversationId) {
    return null;
  }
  const message = messageStash.message;
  messageStash = null;
  return message;
}
